import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import config from '@config/index';
import axios, { isAxiosError, AxiosError, AxiosHeaders, AxiosRequestConfig } from 'axios';

import type { IssueRequest } from '@shared/types';


export const API_BASE = '/api';
//ДЛЯ ДЕБАГА НА ТЕЛЕФОНЕ
// не забудь на Амазоне айпи прописать в корсах

// export const API_BASE = 'http://192.168.0.106:3003/api';
// export const API_BASE = 'http://192.168.1.8:3003/api';
  // export const API_BASE = 'http://192.168.1.107:3001/api';

axios.defaults.baseURL = getBaseUrl();

interface ApiError {
  message: string;
}

type HandleErrorFn = (message: string, statusCode: number | undefined) => void;

const defaultErrorHandler = (
  message: string,
  statusCode: number | undefined
) => {
  if ((statusCode && statusCode >= 500) || !statusCode) {
    toast.error(message);
  }
};

const useAxios = <Response>(
  handleError: HandleErrorFn = defaultErrorHandler,
) => {
  const { t } = useTranslation();
  const [response, setResponse] = useState<Response>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusCode, setStatusCode] = useState<number>();

  const issueRequest: IssueRequest = useCallback(({
    url,
    method,
    body,
    headers,
    query,
    responseType,
  }) => {
    setLoading(true);
    
    const makeRequest = (retryCount = 0): Promise<any> => {
      return axios<Response>({
        method,
        url: url,
        data: body,
        headers,
        params: query,
        responseType,
        withCredentials: true,
      })
        .then((res) => {
          setStatusCode(res.status);
          setResponse(res.data);
        })
        .catch((err: Error | AxiosError) => {
          let message;
          let status;

          if (isAxiosError<ApiError>(err)) {
            if (err.response) {
              status = err.response.status;
              setStatusCode(status);
              
              // Автоматический retry для 429 ошибки
              if (status === 429 && retryCount < 3) {
                const delay = Math.pow(2, retryCount) * 1000; // Экспоненциальная задержка: 1s, 2s, 4s
                
                setTimeout(() => {
                  makeRequest(retryCount + 1);
                }, delay);
                return;
              }
              
              // Специальная обработка для разных статус кодов
              switch (status) {
                case 429:
                  message = t('api.tooManyRequests');
                  break;
                case 401:
                  message = t('api.sessionExpired');
                  // Очищаем localStorage и перенаправляем на логин только если мы не на странице логина
                  try {
                    localStorage.removeItem('authData');
                    // Перенаправляем только если мы не на странице логина
                    if (!window.location.pathname.includes('/login')) {
                      console.log('🔄 Redirecting to login page due to 401');
                      // Используем setTimeout для асинхронного перенаправления
                      setTimeout(() => {
                        window.location.href = '/login';
                      }, 1000);
                    } else {
                      console.log('🔍 Skipping redirect on login page');
                    }
                  } catch (e) {
                    console.error('Ошибка при очистке состояния:', e);
                  }
                  break;
                case 403:
                  message = t('api.accessDenied');
                  break;
                case 404:
                  message = t('api.resourceNotFound');
                  break;
                case 500:
                  message = t('api.serverError');
                  break;
                default:
                  message = typeof err.response.data === 'string' 
                    ? err.response.data 
                    : err.response.data?.message || t('api.requestError');
              }
            } else {
              message = t('api.networkError');
            }
          } else {
            message = t('api.unknownError');
          }

          handleError(message, status);
          setError(message);
        })
        .finally(() => {
          if (retryCount === 0) { // Только для первого запроса
            setLoading(false);
          }
        });
    };

    return makeRequest();
  }, [handleError]);

  return { response, error, issueRequest, loading, statusCode, setLoading };
};

export function getBaseUrl() {
  const { hostname, protocol, port } = window.location;
  
  // Проверяем, локальный ли запуск или IP-адрес
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isLocalIP = /^192\.168\.|^10\.|^172\.(1[6-9]|2[0-9]|3[0-1])\.|^127\./.test(hostname);

  // В режиме разработки
  if (import.meta.env.DEV) {
    // Если мы на localhost, используем localhost для API
    if (isLocalhost) {
      console.log('🔍 Using localhost for API in dev mode');
      return `${protocol}//localhost:${config.serverPort}/api`;
    }
    
    // Если мы на локальном IP (например, с телефона), используем IP компьютера
    if (isLocalIP) {
      // Определяем IP компьютера из URL
      const computerIP = hostname;
      console.log('🔍 Using computer IP for API in dev mode:', computerIP);
      return `${protocol}//${computerIP}:${config.serverPort}/api`;
    }
  }

  // В продакшене используем текущий hostname
  let baseUrl = `${protocol}//${hostname}`;
  if (port && port !== '80' && port !== '443') {
    baseUrl += `:${port}`;
  }
  baseUrl += '/api';
  return baseUrl;
}



export default useAxios;
